package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import kotlin.time.Clock
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.util.createPostsSettings

@Serializable
data class SavedPost(
    val id: String,
    val media: List<MediaItem>,
    val caption: String,
    val platforms: Set<Platform>
)

class SavedPostsViewModel : ViewModel() {
    private val settings = runCatching { createPostsSettings() }.getOrNull()
    private val json = Json { ignoreUnknownKeys = true }

    private val _posts = MutableStateFlow<List<SavedPost>>(load())
    val posts: StateFlow<List<SavedPost>> = _posts.asStateFlow()

    private fun load(): List<SavedPost> {
        val raw = settings?.getStringOrNull(KEY) ?: return emptyList()
        return runCatching { json.decodeFromString<List<SavedPost>>(raw) }.getOrDefault(emptyList())
    }

    private fun persist(posts: List<SavedPost>) {
        settings?.putString(KEY, json.encodeToString(posts))
    }

    fun save(media: List<MediaItem>, caption: String, platforms: Set<Platform>) {
        if (media.isEmpty() && caption.isBlank()) return
        val post = SavedPost(
            id = Clock.System.now().toEpochMilliseconds().toString(),
            media = media,
            caption = caption,
            platforms = platforms
        )
        _posts.update { updated -> (updated + post).also { persist(it) } }
    }

    fun update(id: String, media: List<MediaItem>, caption: String, platforms: Set<Platform>) {
        _posts.update { list ->
            list.map { if (it.id == id) it.copy(media = media, caption = caption, platforms = platforms) else it }
                .also { persist(it) }
        }
    }

    fun delete(id: String) {
        _posts.update { it.filter { p -> p.id != id }.also { persist(it) } }
    }

    fun getById(id: String): SavedPost? = _posts.value.find { it.id == id }

    companion object {
        private const val KEY = "saved_posts"
    }
}
